import React, { useEffect, useState } from 'react';
import { supabase } from './supabase';
import { Dialog, DialogContent } from './ui/arc/dialog';
import { Badge, BadgeTone } from './ui/arc/badge';
import { Button } from './ui/arc/button';
import { Thumb } from './ui/Thumb';
import { NewOrder } from './NewOrder';
import { Pencil, Phone, MapPin, Store, Paperclip, Loader2, StickyNote, ExternalLink } from 'lucide-react';

// Vista rápida de una orden en un popup (20/10/2026), para no salir de la pantalla donde se está
// trabajando (primer uso: Transferencias). Solo lectura + "Editar orden", que abre el mismo popup
// de edición que la card (NewOrder). Lee la orden completa por id: quien la abre solo pasa el id.

const STATUS: Record<string, [string, BadgeTone]> = {
    nueva: ['Nueva', 'info'], falta_insumo: ['Falta insumo', 'warning'], cortado: ['Cortado', 'info'],
    empaquetado: ['Empaquetado', 'info'], despachado: ['Despachado', 'info'], entregado: ['Entregado', 'success'],
    cancelada: ['Cancelada', 'danger'], presupuesto: ['Presupuesto', 'neutral'], stock: ['Stock', 'neutral'],
};
const SOURCE: Record<string, string> = {
    tiendanube: 'Tiendanube', mercadolibre: 'MercadoLibre', manual: 'Manual', ig: 'Instagram',
    whatsapp: 'WhatsApp', showroom: 'Showroom', woocommerce: 'Boutique',
};
const METHOD: Record<string, string> = {
    transferencia: 'Transferencia', transfer: 'Transferencia', efectivo: 'Efectivo', cash: 'Efectivo',
    credit_card: 'Tarjeta de crédito', debit_card: 'Tarjeta de débito', mercadopago: 'Mercado Pago',
    custom: 'Transferencia', offline: 'A convenir', canje: 'Canje',
};

const money = (n: unknown) => '$' + Math.round(Number(n) || 0).toLocaleString('es-AR');
const fmtDateTime = (s?: string | null) => s ? new Date(s).toLocaleString('es-AR', { day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit' }) : '';
export function orderDisplayId(o: { source?: string | null; tiendanube_id?: string | null; id: number }): string {
    if (o.source === 'woocommerce' && o.tiendanube_id) return `#BT-${String(o.tiendanube_id).replace(/^(WC-|BT-)/i, '')}`;
    if ((o.source === 'tiendanube' || o.source === 'mercadolibre') && o.tiendanube_id) return `#${o.tiendanube_id}`;
    return `K-${o.id}`;
}
const isPdf = (u: string) => /\.pdf($|\?)/i.test(u);

export function OrderPeek({ orderId, receipts, onClose, onEdited }: {
    orderId: number | null;
    /** Comprobantes ya calculados por la pantalla que la abre (mismo criterio que la card). */
    receipts?: string[];
    onClose: () => void;
    onEdited?: () => void;
}) {
    const [order, setOrder] = useState<any | null>(null);
    const [images, setImages] = useState<Record<number, string>>({});
    const [error, setError] = useState('');
    const [editing, setEditing] = useState<any | null>(null);
    const [snack, setSnack] = useState('');

    useEffect(() => {
        if (!orderId) { setOrder(null); return; }
        let alive = true;
        setOrder(null); setError(''); setImages({});
        supabase.from('orders').select('*').eq('id', orderId).maybeSingle().then(async ({ data, error }) => {
            if (!alive) return;
            if (error || !data) { setError('No se pudo cargar la orden.'); return; }
            setOrder(data);
            // Fotos: producto directo o a través de la variante (como el PDF).
            const items: any[] = Array.isArray(data.products) ? data.products : [];
            const vIds = items.map(p => Number(p.tn_variant_id)).filter(Boolean);
            const varToProd = new Map<number, number>();
            if (vIds.length) {
                const { data: vs } = await supabase.from('tn_variants').select('id, product_id').in('id', vIds);
                (vs || []).forEach((v: any) => varToProd.set(Number(v.id), Number(v.product_id)));
            }
            const pIds = Array.from(new Set(items.map(p => Number(p.tn_product_id) || varToProd.get(Number(p.tn_variant_id)) || 0).filter(Boolean)));
            if (!pIds.length) return;
            const { data: ps } = await supabase.from('tn_products').select('id, image_url').in('id', pIds);
            if (!alive) return;
            const imgByProd = new Map((ps || []).map((p: any) => [Number(p.id), p.image_url as string]));
            const out: Record<number, string> = {};
            items.forEach((p, i) => {
                const pid = Number(p.tn_product_id) || varToProd.get(Number(p.tn_variant_id));
                const u = pid ? imgByProd.get(pid) : null;
                if (u) out[i] = u;
            });
            setImages(out);
        });
        return () => { alive = false; };
    }, [orderId]);

    const o = order;
    const items: any[] = Array.isArray(o?.products) ? o.products : [];
    const pd = o?.payment_details || {};
    const subtotal = items.reduce((s, p) => s + (Number(p.price) || 0) * (Number(p.quantity) || 1), 0);
    const ship = Number(o?.shipping_cost_customer) || 0;
    const deposit = Number(o?.deposit_amount) || 0;
    const total = Number(o?.total_price) || 0;
    const adjust = Math.round(total - subtotal - ship); // descuentos (medio de pago, cupón, manual) — negativo
    const methodKey = String(pd.payment_method || pd.method || o?.gateway_name || '').toLowerCase();
    const method = METHOD[methodKey] || (methodKey ? methodKey : '');
    const st = STATUS[o?.status] || [o?.status, 'neutral'];
    const phone = String(o?.contact_phone || '').replace(/\D/g, '');
    const recs: string[] = receipts && receipts.length ? receipts : (Array.isArray(o?.receipt_urls) ? o.receipt_urls : []);

    return (
        <>
            <Dialog open={!!orderId && !editing} onOpenChange={v => { if (!v) onClose(); }}>
                <DialogContent
                    title={o ? `${o.contact_name || 'Sin nombre'} · ${orderDisplayId(o)}` : 'Orden'}
                    description={o ? `${SOURCE[o.source] || o.source || ''} · ${fmtDateTime(o.created_at)}` : ''}
                    className="w-[min(640px,calc(100vw-32px))]">
                    {!o ? (
                        <div className="flex items-center justify-center gap-2 py-12 text-soft">
                            {error || <><Loader2 size={16} className="animate-spin" /> Cargando…</>}
                        </div>
                    ) : (
                        <div className="grid gap-4">
                            <div className="flex flex-wrap items-center gap-1.5">
                                <Badge tone={st[1] as BadgeTone}>{st[0]}</Badge>
                                <Badge tone={o.payment_status === 'paid' ? 'success' : 'warning'}>{o.payment_status === 'paid' ? 'Pagada' : 'Pago pendiente'}</Badge>
                                {method && <Badge tone="neutral">{method}{pd.transfer_account ? ` · ${pd.transfer_account === 'krilic' ? 'Krilic' : 'Financiera'}` : ''}</Badge>}
                                {o.is_canje && <Badge tone="info">Canje</Badge>}
                                {o.paid_at && <span className="text-[length:var(--text-xs)] text-soft">pagada el {fmtDateTime(o.paid_at)}</span>}
                            </div>

                            {/* Productos */}
                            <div className="grid divide-y divide-line rounded-lg border border-line">
                                {items.map((p, i) => (
                                    <div key={i} className="flex items-center gap-3 px-3 py-2">
                                        <Thumb src={images[i]} name={p.name} size="sm" />
                                        <span className="min-w-0 flex-1 text-[length:var(--text-sm)] text-ink">
                                            {p.name}{Number(p.quantity) > 1 && <span className="text-soft"> × {p.quantity}</span>}
                                        </span>
                                        <span className="tabular-nums text-[length:var(--text-sm)] text-ink">{money((Number(p.price) || 0) * (Number(p.quantity) || 1))}</span>
                                    </div>
                                ))}
                                {!items.length && <div className="px-3 py-3 text-[length:var(--text-sm)] text-soft">Sin productos</div>}
                            </div>

                            {/* Totales */}
                            <div className="grid gap-1 text-[length:var(--text-sm)]">
                                {(adjust !== 0 || ship > 0) && <Line label="Productos" value={money(subtotal)} />}
                                {adjust !== 0 && <Line label={adjust < 0 ? 'Descuentos' : 'Ajustes'} value={(adjust < 0 ? '−' : '+') + money(Math.abs(adjust))} />}
                                {ship > 0 && <Line label="Envío" value={money(ship)} />}
                                <Line label="Total" value={money(total)} strong />
                                {deposit > 0 && o.payment_status !== 'paid' && <Line label={`Seña ${money(deposit)} · resta`} value={money(total - deposit)} />}
                            </div>

                            {/* Contacto y entrega */}
                            <div className="grid gap-1.5 text-[length:var(--text-sm)] text-ink">
                                {phone && (
                                    <a className="flex items-center gap-2 text-ink hover:underline" href={`https://wa.me/${phone.length <= 10 ? '549' + phone : phone}`} target="_blank" rel="noreferrer">
                                        <Phone size={14} className="text-soft" /> {o.contact_phone}
                                    </a>
                                )}
                                <span className="flex items-start gap-2">
                                    {o.delivery_type === 'retiro' ? <Store size={14} className="mt-0.5 text-soft" /> : <MapPin size={14} className="mt-0.5 text-soft" />}
                                    {o.delivery_type === 'retiro' ? 'Retira en el showroom' : [o.delivery_address, o.delivery_unit].filter(Boolean).join(' · ') || 'Envío (sin dirección)'}
                                </span>
                                {o.note && <span className="flex items-start gap-2 whitespace-pre-wrap"><StickyNote size={14} className="mt-0.5 text-soft" /> {o.note}</span>}
                            </div>

                            {/* Comprobantes */}
                            {recs.length > 0 && (
                                <div className="grid gap-2">
                                    <span className="flex items-center gap-1.5 text-[length:var(--text-xs)] font-semibold uppercase tracking-wide text-soft"><Paperclip size={12} /> Comprobantes</span>
                                    <div className="flex flex-wrap gap-2">
                                        {recs.map(u => (
                                            <a key={u} href={u} target="_blank" rel="noreferrer" title="Abrir en otra pestaña"
                                                className="flex h-20 w-20 items-center justify-center overflow-hidden rounded-md border border-line bg-[var(--card)] text-[length:var(--text-xs)] text-soft hover:opacity-80">
                                                {isPdf(u) ? <span className="flex flex-col items-center gap-1"><ExternalLink size={16} /> PDF</span> : <img src={u} alt="comprobante" className="h-full w-full object-cover" />}
                                            </a>
                                        ))}
                                    </div>
                                </div>
                            )}

                            <div className="flex justify-end gap-2 border-t border-line pt-4">
                                <Button variant="secondary" onClick={onClose}>Cerrar</Button>
                                <Button onClick={() => setEditing(o)}><Pencil size={14} /> Editar orden</Button>
                            </div>
                            {snack && <span className="text-right text-[length:var(--text-xs)] text-soft">{snack}</span>}
                        </div>
                    )}
                </DialogContent>
            </Dialog>

            {editing && (
                <NewOrder
                    initialOrder={editing}
                    onCancel={() => setEditing(null)}
                    onSuccess={() => {
                        setEditing(null);
                        // Recargar la orden del popup y avisar a la pantalla para que recalcule.
                        supabase.from('orders').select('*').eq('id', editing.id).maybeSingle().then(({ data }) => { if (data) setOrder(data); });
                        onEdited?.();
                    }}
                    showSnackbar={(msg) => setSnack(msg)}
                />
            )}
        </>
    );
}

function Line({ label, value, strong }: { label: string; value: string; strong?: boolean }) {
    return (
        <div className={`flex justify-between gap-4 ${strong ? 'border-t border-line pt-1.5 font-semibold text-ink' : 'text-soft'}`}>
            <span>{label}</span><span className="tabular-nums">{value}</span>
        </div>
    );
}
